/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Upload, X, Image as ImageIcon, Video as VideoIcon, FileText } from 'lucide-react';
import { getMediaCategory } from '../constants/mimeMap';
import { MAX_FILE_SIZE_BYTES } from '../utils/multipartUpload';

/**
 * Upload control for an optional reference image/video (and, when
 * `allowDocument` is set, PDF) attached to a capture step or — via
 * WorkflowBuilder's Global Instructions field — to the whole workflow.
 * Modeled on FileUpload.jsx's interaction pattern (drag/drop, spinner,
 * replace/remove). Kept as its own component so FileUpload.jsx — shared by
 * the brand logo / favicon / workflow logo flows — stays untouched.
 */
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];
const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const DOCUMENT_TYPES = ['application/pdf'];
const IMAGE_MAX_SIZE = 200 * 1024 * 1024;
/** Matches the backend's REFERENCE_MEDIA_MAX_SIZE / the capture flow's multipart ceiling */
const NON_IMAGE_MAX_SIZE = MAX_FILE_SIZE_BYTES;

const StepReferenceMediaUpload = ({
  id,
  label = 'Reference Media (optional)',
  currentUrl = '',
  onUploadComplete,
  onClear,
  uploadFn,
  onUploadStateChange,
  disabled = false,
  allowDocument = false,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const allowedTypes = allowDocument ? [...IMAGE_TYPES, ...VIDEO_TYPES, ...DOCUMENT_TYPES] : [...IMAGE_TYPES, ...VIDEO_TYPES];

  const setUploadingState = (value) => {
    setUploading(value);
    onUploadStateChange?.(value);
  };

  const validate = (file) => {
    if (!allowedTypes.includes(file.type)) {
      return `Only PNG, JPG/JPEG, MP4, WEBM, or MOV${allowDocument ? ', or PDF' : ''} files are allowed`;
    }
    const maxSize = IMAGE_TYPES.includes(file.type) ? IMAGE_MAX_SIZE : NON_IMAGE_MAX_SIZE;
    if (file.size > maxSize) {
      return `File size must not exceed ${Math.round(maxSize / (1024 * 1024))}MB`;
    }
    return null;
  };

  const handleFile = async (file) => {
    if (!file) return;

    const validationError = validate(file);
    if (validationError) {
      setError(validationError);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setError('');
    setUploadingState(true);
    try {
      const result = await uploadFn(file);
      onUploadComplete(result.url);
    } catch (err) {
      const msg = err?.response?.data?.message;
      setError(Array.isArray(msg) ? msg[0] : (msg || 'Upload failed. Please try again.'));
    } finally {
      setUploadingState(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleInputChange = (e) => {
    handleFile(e.target.files?.[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || uploading) return;
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handleClear = () => {
    setError('');
    onClear();
    if (inputRef.current) inputRef.current.value = '';
  };

  const currentCategory = currentUrl ? getMediaCategory(null, currentUrl) : null;
  const isCurrentVideo = currentCategory === 'video';
  const isCurrentDocument = currentCategory === 'document';

  return (
    <div>
      {label && (
        <span className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block mb-2">
          {label}
        </span>
      )}

      {currentUrl && !uploading && (
        <div className="mb-3 relative inline-block">
          {isCurrentDocument ? (
            <div className="h-16 w-16 flex flex-col items-center justify-center gap-1 rounded-lg border border-base-300 bg-base-100">
              <FileText size={20} className="text-base-content/50" />
              <span className="text-[8px] font-bold text-base-content/40 uppercase">PDF</span>
            </div>
          ) : isCurrentVideo ? (
            <video
              src={currentUrl}
              muted
              playsInline
              className="h-16 w-16 object-cover rounded-lg border border-base-300 bg-base-100"
            />
          ) : (
            <img
              src={currentUrl}
              alt="Reference media"
              className="h-16 w-16 object-cover rounded-lg border border-base-300 bg-base-100"
            />
          )}
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              title="Remove reference media"
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-base-300 rounded-full flex items-center justify-center shadow-sm"
            >
              <X size={11} strokeWidth={3} />
            </button>
          )}
        </div>
      )}

      {(!currentUrl || uploading) && (
        <div
          onDragOver={(e) => { e.preventDefault(); if (!disabled && !uploading) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && !uploading && inputRef.current?.click()}
          className={`
            relative flex flex-col items-center justify-center gap-2
            border-2 border-dashed rounded-xl px-4 py-5 transition-all
            ${disabled ? 'cursor-not-allowed opacity-50 bg-base-200 border-base-300' : ''}
            ${!disabled && !uploading ? 'cursor-pointer' : ''}
            ${dragOver ? 'border-primary bg-primary/5' : 'border-base-300 hover:border-primary/50 hover:bg-base-200/50'}
            ${uploading ? 'border-primary/30 bg-primary/5' : ''}
          `}
        >
          {uploading ? (
            <>
              <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
              <span className="text-xs font-bold text-base-content/50">Uploading...</span>
            </>
          ) : (
            <>
              <div className="w-8 h-8 bg-base-200 rounded-lg flex items-center justify-center">
                {dragOver ? <ImageIcon size={16} className="text-primary" /> : <Upload size={16} className="text-base-content/30" />}
              </div>
              <div className="text-center">
                <p className="text-xs font-bold text-base-content/50">
                  {dragOver ? 'Drop to upload' : allowDocument ? 'Click or drag & drop a file' : 'Click or drag & drop an image or video'}
                </p>
                <p className="text-[10px] text-base-content/30 mt-0.5 flex items-center gap-1 justify-center">
                  <ImageIcon size={10} /> PNG, JPG &nbsp;•&nbsp; <VideoIcon size={10} /> MP4, WEBM, MOV
                  {allowDocument && <>&nbsp;•&nbsp; <FileText size={10} /> PDF</>}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {currentUrl && !uploading && !disabled && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="mt-2 flex items-center gap-1.5 text-[10px] font-bold text-base-content/40 hover:text-primary transition"
        >
          <Upload size={11} />
          Replace media
        </button>
      )}
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={`image/png,image/jpeg,video/mp4,video/webm,video/quicktime${allowDocument ? ',application/pdf' : ''}`}
        disabled={disabled || uploading}
        onChange={handleInputChange}
        className="sr-only"
      />

      {error && (
        <p className="mt-1.5 text-xs font-medium text-error">{error}</p>
      )}
    </div>
  );
};

StepReferenceMediaUpload.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  currentUrl: PropTypes.string,
  onUploadComplete: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  uploadFn: PropTypes.func.isRequired,
  onUploadStateChange: PropTypes.func,
  disabled: PropTypes.bool,
  allowDocument: PropTypes.bool,
};

export default StepReferenceMediaUpload;
